import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CallProvider } from "@/context/CallContext";
import { CallWidget } from "@/components/CallWidget";
import AppShell from "@/components/AppShell";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Home from "@/pages/Home";
import Planning from "@/pages/Planning";
import PlanningIdeal from "@/pages/PlanningIdeal";
import PlanningIdealPap from "@/pages/PlanningIdealPap";
import PlanningPAP from "@/pages/PlanningPAP";
import BanqueSpots from "@/pages/BanqueSpots";
import FicheSpot from "@/pages/FicheSpot";
import ImportBDD from "@/pages/ImportBDD";
import Admin from "@/pages/Admin";
import PrimesRebooking from "@/pages/PrimesRebooking";
import Prospection from "@/pages/Prospection";
import Emails from "@/pages/Emails";
import Bulletins from "@/pages/Bulletins";
import RdFiche from "@/pages/RdFiche";
import MesObjectifs from "@/pages/MesObjectifs";
import SuiviCDP from "@/pages/SuiviCDP";
import Confirmation from "@/pages/Confirmation";
import Appels from "@/pages/Appels";
import Formation from "@/pages/Formation";
import Negotiations from "@/pages/Negotiations";
import ExceptionsJournal from "@/pages/ExceptionsJournal";
import Tendances from "@/pages/Tendances";
import SuiviCoordination from "@/pages/SuiviCoordination";
import Pointage from "@/pages/Pointage";
import PointageResp from "@/pages/PointageResp";
import AdminPointage from "@/pages/AdminPointage";
import AdminKpis from "@/pages/AdminKpis";
import TerrainDuJour from "@/pages/TerrainDuJour";
import Equipes from "@/pages/Equipes";
import ConfirmationsMagasins from "@/pages/ConfirmationsMagasins";
import DoublonsSpots from "@/pages/DoublonsSpots";
import Annonces from "@/pages/Annonces";

function Protected({ children, roles, feature }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--bg-subtle)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--shariti-orange)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  // Rôle « Lecture Seule » : accès au Planning site privé + les tableaux terrain
  // cochés (board_pap / board_rue_itinerance / board_rue_fixe) + mot de passe.
  if (user.role === "lecture_seule") {
    const boardRoutes = {
      board_pap: "/planning-pap",
      board_rue_itinerance: "/planning-rue-itinerance",
      board_rue_fixe: "/planning-rue-fixe",
    };
    const perms = user.permissions || {};
    const allowedBoards = Object.entries(boardRoutes).filter(([k]) => perms[k]).map(([, v]) => v);
    const allowed = ["/planning", "/change-password", "/equipes", "/terrain-jour", ...allowedBoards];
    // Chargée des opérations : accès à la page « Suivi coordination » si la permission est activée.
    if (perms.declare_coordination) allowed.push("/suivi-coordination");
    // Diffusion d'annonces (chargé des opérations autorisé).
    if (perms.broadcast) allowed.push("/annonces");
    if (!allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      return <Navigate to="/planning" replace />;
    }
  }
  // Rôle « Porte-à-porte » : accès restreint au planning porte-à-porte et à son
  // Planning Idéal (aide au démarchage terrain) uniquement.
  if (user.role === "pap") {
    const allowed = ["/planning-pap", "/planning-ideal-pap", "/change-password"];
    if (!allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      return <Navigate to="/planning-pap" replace />;
    }
  }
  // Rôle « Responsable planning terrain » : accès aux seuls tableaux cochés.
  // (Le suivi du planning « site privé » se fait via le lien PERSONNEL du responsable
  //  d'équipe — /equipe/{token} — filtré sur SES lignes uniquement.)
  if (user.role === "terrain") {
    const boardRoutes = {
      board_pap: "/planning-pap",
      board_rue_itinerance: "/planning-rue-itinerance",
      board_rue_fixe: "/planning-rue-fixe",
    };
    const perms = user.permissions || {};
    const allowedBoards = Object.entries(boardRoutes).filter(([k]) => perms[k]).map(([, v]) => v);
    const allowed = [...allowedBoards, "/change-password"];
    const ok = allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to={allowedBoards[0] || "/change-password"} replace />;
  }
  // Rôle « Prospecteur terrain » : agent MOBILE (aucune zone). Il aide les CDP à
  // booker partout → accès aux spots, au planning (lecture + booking), aux appels
  // et aux emails/négociations. Aucun accès admin/import/pointage.
  if (user.role === "prospecteur") {
    const allowed = ["/planning", "/spots", "/appels", "/emails", "/negociations", "/change-password"];
    const ok = allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/planning" replace />;
  }
  // Rôle « Superviseur » : CONSULTATION SEULE sur un périmètre restreint de pages
  // (aligné sur la liste blanche backend SUPERVISEUR_GET_ALLOW). Les données « Suivi CDP »
  // sont limitées côté serveur aux CDP qui lui sont attribués. Aucun accès à la
  // Facturation, au Pointage, aux Bulletins, à l'Import ni à l'Administration.
  if (user.role === "superviseur") {
    const allowed = [
      "/planning", "/planning-ideal", "/spots", "/doublons-spots", "/prospection",
      "/appels", "/formation", "/suivi-cdp", "/tendances", "/exceptions", "/emails",
      "/negociations", "/confirmations-magasins", "/rds", "/change-password",
    ];
    const ok = allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/suivi-cdp" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // Module masqué par l'admin pour cet utilisateur (ex : « bulletins »).
  if (feature && (user.hidden_features || []).includes(feature)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CallProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/confirmation/:token" element={<Confirmation />} />
              <Route path="/pointage/:token" element={<Pointage />} />
              <Route path="/equipe/:token" element={<PointageResp />} />
              <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
              <Route path="/" element={<Protected><Home /></Protected>} />
              <Route path="/planning" element={<Protected><Planning /></Protected>} />
              <Route path="/planning-ideal" element={<Protected roles={["admin", "cdp", "superviseur"]}><PlanningIdeal /></Protected>} />
              <Route path="/planning-ideal-pap" element={<Protected roles={["admin", "pap"]}><PlanningIdealPap /></Protected>} />
              <Route path="/planning-pap" element={<Protected roles={["admin", "pap", "terrain", "lecture_seule"]}><PlanningPAP category="pap" /></Protected>} />
              <Route path="/planning-rue-itinerance" element={<Protected roles={["admin", "terrain", "lecture_seule"]}><PlanningPAP category="rue_itinerance" /></Protected>} />
              <Route path="/planning-rue-fixe" element={<Protected roles={["admin", "terrain", "lecture_seule"]}><PlanningPAP category="rue_fixe" /></Protected>} />
              <Route path="/spots" element={<Protected roles={["admin", "cdp", "prospecteur", "superviseur"]}><BanqueSpots /></Protected>} />
              <Route path="/doublons-spots" element={<Protected roles={["admin", "cdp", "superviseur"]}><DoublonsSpots /></Protected>} />
              <Route path="/spots/:id" element={<Protected roles={["admin", "cdp", "prospecteur", "superviseur"]}><FicheSpot /></Protected>} />
              <Route path="/prospection" element={<Protected roles={["admin", "cdp", "superviseur"]}><Prospection /></Protected>} />
              <Route path="/appels" element={<Protected roles={["admin", "cdp", "prospecteur", "superviseur"]}><Appels /></Protected>} />
              <Route path="/formation" element={<Protected roles={["admin", "cdp", "superviseur"]}><Formation /></Protected>} />
              <Route path="/objectifs" element={<Protected roles={["cdp", "admin"]}><MesObjectifs /></Protected>} />
              <Route path="/suivi-cdp" element={<Protected roles={["admin", "superviseur"]}><SuiviCDP /></Protected>} />
              <Route path="/primes-rebooking" element={<Protected roles={["admin"]}><PrimesRebooking /></Protected>} />
              <Route path="/tendances" element={<Protected roles={["admin", "superviseur"]}><Tendances /></Protected>} />
              <Route path="/kpis-equipes" element={<Protected roles={["admin"]}><AdminKpis /></Protected>} />
              <Route path="/pointage" element={<Protected roles={["admin"]}><AdminPointage /></Protected>} />
              <Route path="/terrain-jour" element={<Protected roles={["admin", "lecture_seule"]}><TerrainDuJour /></Protected>} />
              <Route path="/equipes" element={<Protected roles={["admin", "lecture_seule"]}><Equipes /></Protected>} />
              <Route path="/exceptions" element={<Protected roles={["admin", "superviseur"]}><ExceptionsJournal /></Protected>} />
              <Route path="/suivi-coordination" element={<Protected roles={["admin", "lecture_seule"]}><SuiviCoordination /></Protected>} />
              <Route path="/annonces" element={<Protected roles={["admin", "lecture_seule"]}><Annonces /></Protected>} />
              <Route path="/emails" element={<Protected roles={["admin", "cdp", "prospecteur", "superviseur"]}><Emails /></Protected>} />
              <Route path="/negociations" element={<Protected roles={["admin", "cdp", "prospecteur", "superviseur"]}><Negotiations /></Protected>} />
              <Route path="/confirmations-magasins" element={<Protected roles={["admin", "superviseur"]}><ConfirmationsMagasins /></Protected>} />
              <Route path="/bulletins" element={<Protected feature="bulletins"><Bulletins /></Protected>} />
              <Route path="/rds/:id" element={<Protected><RdFiche /></Protected>} />
              <Route path="/import" element={<Protected roles={["admin"]}><ImportBDD /></Protected>} />
              <Route path="/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CallWidget />
            <Toaster position="top-right" richColors closeButton />
          </CallProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
